import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Search,
  Pencil,
  Trash2,
  Music,
  Globe,
  Loader2,
  RefreshCw,
  Database,
  Save,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  Check,
  ExternalLink,
  GitMerge,
  MapPin,
  Drama,
  Clapperboard,
  Shapes,
} from "lucide-react";
import { Button } from "../../components/ui/Button";
import { Pagination } from "../../components/ui/Pagination";
import { PreferredLabel } from "../../components/ui/PreferredLabel";
import { ProviderLabel } from "../../components/ui/ProviderLabel";
import { Card, CardContent } from "../../components/ui/Card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/Table";
import { TableColumnHeader } from "../../components/ui/TableColumnHeader";
import { loadCities } from "../../components/ui/CitySelect";
import { MobileFilterBar, chip } from "../../components/ui/MobileFilterBar";
import { Badge, badgeVariants } from "../../components/ui/Badge";
import { ClassificationProfileDetail, JudgmentPicker } from "../../components/ui/ClassificationProfileDetail";
import {
  CLASSIFICATION_OPTIONS,
  classificationProfileToEdit,
  buildClassificationProfile,
  changedClassificationFields,
} from "./classificationEdit";
import { EntityLink } from "../../components/ui/EntityLink";
import { cn } from "../../utils/cn";
import { SearchFilterForm, useSearchFilter } from "../../components/ui/SearchFilterForm";
import { Modal, ModalContent, ModalFooter } from "../../components/ui/Modal";
import { EntitySearchPicker } from "../../components/ui/EntitySearchPicker";
import ImageListEditor from "../../components/ui/ImageListEditor";
import {
  CLASSIFIED_FILTER_OPTIONS,
  ClassificationIssueBadge,
  classifiedFilterParams,
  ClassifyAction,
  useClassify,
} from "../../components/ui/ClassifyAction";
import { ReviewEventLink } from "../../components/ui/DuplicateReview";
import ArtistDuplicatesView from "./ArtistDuplicatesView";
import DeletedArtistsView from "./DeletedArtistsView";
import { artistsApi, genresApi, eventsApi } from "../../utils/api";
import { useEntityDeepLink } from "../../hooks/useEntityDeepLink";
import { useSuspiciousImages } from "../../hooks/useSuspiciousImages";
import { searchArtists as mbSearchArtists, formatArtistData as mbFormatArtist, searchArtistBySpotifyId as mbSearchBySpotify, searchArtistByYoutubeUrl as mbSearchByYoutube } from "../../utils/musicBrainzApi";
import { parseCommaSeparated, displayLabel, arraysEqual, capitalize, formatDate, hasClassificationProfile, normalizeImageRefs as normalizeArtistImages, reconcileImageUrls, artistDeleteNeedsAttention, definedEntries } from "../../utils/helpers";

const PAGE_SIZE = 20;
const MBID_SEARCH_PAGE = 10; // MusicBrainz name-search results per "Show more" step
const MBID_SEARCH_MAX = 100; // API cap on /admin/artists/musicbrainz-search?limit
// Hoisted: constant inputs, no need to re-run cva/twMerge per chip per render.
const GENRE_CHIP_CLASS = cn(badgeVariants({ variant: "secondary" }), "hover:bg-destructive/10 hover:text-destructive");
// Sortable columns, in table order. Rendered as header buttons on desktop and as the
// mobile sort chip row, so the two can't offer different sorts.
const ARTIST_SORT_COLUMNS = [
  { key: "upcoming_event_count", label: "Upcoming" },
  { key: "user_count", label: "Favorited by" },
  { key: "first_seen", label: "First Seen" },
];

const SORT_COLUMN = Object.fromEntries(ARTIST_SORT_COLUMNS.map((c) => [c.key, c]));

// Icon for the artist's `domain`, first match wins. Matched loosely because the value is
// whatever a provider wrote and the normalizers disagree — Ticketmaster's segments
// ("Arts & Theatre", "Film", "Miscellaneous") sit next to JamBase's "Music" and OpenData
// Madrid's "Arts & Culture". No domain, or one nothing here matches, is Miscellaneous —
// the note is for artists actually tagged as music.
const DOMAIN_ICONS = [
  [/theat|arts|cultur/i, Drama],
  [/film|cinema/i, Clapperboard],
  [/misc/i, Shapes],
  [/music/i, Music],
];

function domainIcon(domains) {
  for (const value of domains || []) {
    const match = DOMAIN_ICONS.find(([pattern]) => pattern.test(value));
    if (match) return match[1];
  }
  return Shapes;
}

const YES_NO_FILTER_OPTIONS = [
  { value: "", label: "All" },
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
];
// The "Social media?" column: any `links` entry counts except MusicBrainz and JamBase, catalogue
// entries rather than places the act posts. Mirrors the `ignore` params of ES migration 031 — the
// server filters on that rule and this renders it, and the two disagreeing is exactly what shows
// a Yes on a row the "No" filter returned.
const NON_SOCIAL_LINK_KEYS = ["musicbrainz", "jambase"];
const hasSocialLinks = (links) =>
  Object.entries(links || {}).some(
    ([key, url]) => !NON_SOCIAL_LINK_KEYS.includes(key) && String(url || "").trim()
  );

const objectToRows = (obj = {}, prefix = "row") =>
  definedEntries(obj).map(([key, value]) => ({ _key: `${prefix}-${key}`, key, value: value || "" }));

// Artist images may be {url, source} objects or legacy bare URL strings.
const rowsToObject = (rows = []) => {
  const result = {};
  rows.forEach((row) => {
    const key = row.key?.trim().toLowerCase();
    const value = row.value?.trim();
    if (key && value) result[key] = value;
  });
  return result;
};

// A labelled row of genre chips, each linking to its genre.
const GenreChips = ({ title, genres, variant, empty, children }) => (
  <div>
    <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-2">{title}</p>
    <div className="flex flex-wrap gap-1">
      {(genres || []).map((g) => (
        <EntityLink key={g.id || g.name} type="genre" id={g.id}>
          <Badge variant={variant}>{g.name}</Badge>
        </EntityLink>
      ))}
      {!(genres || []).length && <span className="text-muted-foreground text-sm">{empty}</span>}
    </div>
    {children}
  </div>
);

const genresToRows = (genres = []) =>
  (genres || []).map((genre, index) => ({
    _key: (typeof genre === "string" ? genre : genre.id) || `genre-${index}`,
    id: typeof genre === "string" ? genre : genre.id || "",
    name: typeof genre === "string" ? genre : genre.name || "",
  }));

const rowsToGenres = (rows = []) =>
  (rows || [])
    .map((row) => ({ id: row.id?.trim(), name: row.name?.trim() }))
    .filter((row) => row.id);

// This list is the artist's referencing events — past, cancelled and non-active ones included
// — so it doubles as the act's booking history. It is one page of them, so a truncated list
// says so rather than reading as the whole history. `ReviewEventLink` renders one exactly
// as the duplicate queues do (title → the event in the admin, icon → its Applauss page, and a
// meta line saying which of them are past), but it wants the summary shape
// `_artist_review_events` builds server-side. The admin event list returns raw occurrences, so
// derive it here, matching `compact_review_event` field for field — including its UTC "today",
// so the same event cannot read "past" in one admin list and not in another. Dates compare as
// strings; parsing local_date as a Date shifts the day.
const summarizeArtistEvent = (event) => {
  const occurrences = event.occurrences || [];
  const dates = occurrences.map((occ) => occ.local_date).filter(Boolean).sort();
  const last = dates[dates.length - 1];
  return {
    ...event,
    id: event.id || event._id,
    start_date: dates[0] || null,
    end_date: last || null,
    is_past: Boolean(last) && last < new Date().toISOString().slice(0, 10),
    is_cancelled:
      occurrences.length > 0 && occurrences.every((occ) => String(occ.status || "").toLowerCase() === "cancelled"),
  };
};

const normalizeArtist = (artist = {}) => {
  const sourceIds = { ...(artist.source_ids || {}) };
  if (artist.spotify_id) sourceIds.spotify = sourceIds.spotify || artist.spotify_id;
  if (artist.musicbrainz_id) sourceIds.musicbrainz = sourceIds.musicbrainz || artist.musicbrainz_id;
  return { ...artist, source_ids: sourceIds };
};

// POST /admin/artists/{id}/merge-into/{canonical} always answers with a plain string `detail`.
const mergeErrorMessage = (err) => err.response?.data?.detail || "Could not merge artist";

// What a merge does to the artist being folded in — same sentence in both places that offer one.
const MERGE_EFFECT_COPY =
  "its events, users, source IDs, links, images and genres are absorbed, and it is retained as a duplicate.";

function MergeIntoButton({ onClick, disabled, busy }) {
  return (
    <Button type="button" size="sm" variant="outline" onClick={onClick} disabled={disabled}>
      {busy ? (
        <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Merging...</>
      ) : (
        <><GitMerge className="h-4 w-4 mr-2" />Merge into this artist</>
      )}
    </Button>
  );
}

function ClassificationMultiSelect({ label, options, value, onChange }) {
  const selected = new Set(parseCommaSeparated(value));
  const toggle = (option) => {
    const next = new Set(selected);
    if (next.has(option)) {
      next.delete(option);
    } else {
      next.add(option);
    }
    onChange(Array.from(next).join(", "));
  };

  return (
    <div>
      <label className="text-sm font-medium">{label}</label>
      <div className="mt-1 grid max-h-44 grid-cols-1 gap-1 overflow-y-auto rounded-md border border-input bg-background p-2 sm:grid-cols-2">
        {options.map((option) => (
          <label key={option} className="flex min-w-0 items-center gap-2 rounded px-2 py-1 text-sm hover:bg-muted/60">
            <input
              type="checkbox"
              checked={selected.has(option)}
              onChange={() => toggle(option)}
              className="h-4 w-4 rounded border-input accent-primary"
            />
            <span className="min-w-0 truncate" title={option}>{capitalize(option)}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

export default function ArtistsPage() {
  const [artists, setArtists] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [backgroundLoading, setBackgroundLoading] = useState(false);
  const { searchInput, setSearchInput, search: searchFilter, submitSearch, clearSearch } = useSearchFilter();
  const [countryInput, setCountryInput] = useState("");
  const [countryFilter, setCountryFilter] = useState("");
  // "" = all, "yes" = has classification_profile, "no" = missing
  const [classifiedFilter, setClassifiedFilter] = useState("");
  // "" = all, "yes" = has musicbrainz id, "no" = missing
  const [musicbrainzFilter, setMusicbrainzFilter] = useState("");
  // "" = all, "yes" = has at least one social-media link, "no" = none at all
  const [socialFilter, setSocialFilter] = useState("");
  // "" = all, else one `domain` value. Options come from the API, not a hardcoded
  // list — a provider adding a segment must not need a deploy to be filterable.
  const [domainFilter, setDomainFilter] = useState("");
  const [domainOptions, setDomainOptions] = useState([]);
  // "" = all cities. Filters the list to artists playing there *and* scopes the upcoming
  // count to that city, which is why it sits on the Upcoming column.
  const [cityFilter, setCityFilter] = useState("");
  const [cityOptions, setCityOptions] = useState([]);
  const [page, setPage] = useState(1);
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Detail modal
  const [selectedArtist, setSelectedArtist] = useState(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  // Classify / Re-classify on the detail modal: one synchronous LLM call. The new profile is
  // copied to every event that inherits it.
  const classify = useClassify(artistsApi.classify);

  // Edit modal
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editData, setEditData] = useState({});
  const { suspiciousUrls: editSuspiciousImages, check: checkSuspiciousImages } = useSuspiciousImages();
  const [isSaving, setIsSaving] = useState(false);
  const [genreSearch, setGenreSearch] = useState("");
  const [genreResults, setGenreResults] = useState([]);
  const [genreLoading, setGenreLoading] = useState(false);

  // Delete modal
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteImpact, setDeleteImpact] = useState(null);

  // Top-left tab: artists table vs. duplicate review queue
  const [activeTab, setActiveTab] = useState("artists");
  const [artistDuplicateCount, setArtistDuplicateCount] = useState(0);
  const [dupRefreshSignal, setDupRefreshSignal] = useState(0); // bumped to force the duplicates view to reload

  // Manual duplicate: pick an artist to fold into `selectedArtist` (the canonical).
  const [isMarkDuplicateOpen, setIsMarkDuplicateOpen] = useState(false);
  const [dupPicked, setDupPicked] = useState(null);
  const [dupBusy, setDupBusy] = useState(false);
  const [dupError, setDupError] = useState(null);

  // Change MusicBrainz ID modal
  const [isMbidModalOpen, setIsMbidModalOpen] = useState(false);
  const [mbidSearchQuery, setMbidSearchQuery] = useState("");
  const [mbidSearchResults, setMbidSearchResults] = useState([]);
  const [mbidSearchLoading, setMbidSearchLoading] = useState(false);
  const [mbidSearchError, setMbidSearchError] = useState(null);
  const [mbidSelected, setMbidSelected] = useState(null); // formatted MB artist
  const [mbidCheck, setMbidCheck] = useState(null); // { available, conflicting_artists }
  const [mbidChecking, setMbidChecking] = useState(false);
  const [mbidSaving, setMbidSaving] = useState(false);
  const [mbidKeepClassification, setMbidKeepClassification] = useState(null); // null=unanswered, true, false
  // "spotify" | "name" | null — tracks where the visible results came from
  const [mbidResultsSource, setMbidResultsSource] = useState(null);
  // How many name-search hits to ask MusicBrainz for. "Show more" bumps it and refetches;
  // MB search has no cursor, so a wider limit is the whole pagination story (API caps at 100).
  const [mbidSearchLimit, setMbidSearchLimit] = useState(MBID_SEARCH_PAGE);
  const mbidDebounceRef = useRef(null);

  // Artist events (shown in detail modal)
  const [artistEvents, setArtistEvents] = useState([]);
  const [artistEventsLoading, setArtistEventsLoading] = useState(false);

  const fetchArtists = useCallback(async ({ silent = false } = {}) => {
    if (silent) setBackgroundLoading(true);
    else setLoading(true);
    setError(null);
    try {
      const params = { page, page_size: PAGE_SIZE };
      if (searchFilter) params.search = searchFilter;
      if (countryFilter) params.country = countryFilter;
      if (sortKey) {
        params.sort_by = sortKey;
        params.sort_order = sortDir;
      }
      Object.assign(params, classifiedFilterParams(classifiedFilter));
      if (musicbrainzFilter === "yes") params.has_musicbrainz = true;
      else if (musicbrainzFilter === "no") params.has_musicbrainz = false;
      if (socialFilter === "yes") params.has_social_links = true;
      else if (socialFilter === "no") params.has_social_links = false;
      if (domainFilter) params.domain = domainFilter;
      if (cityFilter) params.city = cityFilter;

      const data = await artistsApi.getAll(params);
      setArtists((data.artists || []).map(normalizeArtist));
      setPagination(data.pagination || null);
    } catch (err) {
      console.error("Error fetching artists:", err);
      if (!err.response) {
        setError("Could not connect to the server.");
      } else {
        setError(err.response?.data?.detail || "Error loading artists");
      }
    } finally {
      if (silent) setBackgroundLoading(false);
      else setLoading(false);
    }
  }, [page, searchFilter, countryFilter, sortKey, sortDir, classifiedFilter, musicbrainzFilter, socialFilter, domainFilter, cityFilter]);

  useEffect(() => {
    fetchArtists();
  }, [fetchArtists]);

  // Filter options, loaded once. A failure leaves the dropdown with just "All" rather
  // than blocking the table.
  useEffect(() => {
    artistsApi
      .getDomains()
      .then((d) => setDomainOptions(d.domains || []))
      .catch(() => {});
    loadCities().then(setCityOptions);
  }, []);

  // Reloads the "Duplicates (N)" tab badge. `reloadView` also forces the duplicates view itself
  // to refetch — every merge or MBID assignment can resolve or auto-merge pairs behind it.
  const refreshDuplicateQueue = useCallback((reloadView = false) => {
    if (reloadView) setDupRefreshSignal((s) => s + 1);
    artistsApi
      .getDuplicates(1, 1)
      .then((d) => setArtistDuplicateCount(d.total_groups || 0))
      .catch(() => {});
  }, []);

  useEffect(() => {
    refreshDuplicateQueue();
  }, [refreshDuplicateQueue]);

  // Closing the detail modal always drops its per-artist state and re-reads the table, which may
  // have changed underneath it (edit, merge, MBID change).
  const closeDetailModal = useCallback(() => {
    setIsDetailModalOpen(false);
    setArtistEvents([]);
    setArtistEventsLoading(false);
    setSelectedArtist(null);
    fetchArtists({ silent: true });
  }, [fetchArtists]);

  // The candidate + its availability verdict + the keep/replace answer are one unit: an answer
  // that outlives the candidate it was given for would be applied to the next one.
  const resetMbidSelection = () => {
    setMbidSelected(null);
    setMbidCheck(null);
    setMbidKeepClassification(null);
  };

  useEffect(() => {
    if (!success) return;
    const t = setTimeout(() => setSuccess(null), 4000);
    return () => clearTimeout(t);
  }, [success]);

  useEffect(() => {
    if (!error) return;
    const t = setTimeout(() => setError(null), 4000);
    return () => clearTimeout(t);
  }, [error]);

  const handleSearch = (e) => {
    submitSearch(e);
    setPage(1);
    setCountryFilter(countryInput.trim().toUpperCase());
  };

  const handleClearSearch = () => {
    clearSearch();
    setCountryInput("");
    setCountryFilter("");
    setPage(1);
  };

  const handleSort = (key) => {
    const newDir = sortKey === key ? (sortDir === "asc" ? "desc" : "asc") : "asc";
    setSortKey(key);
    setSortDir(newDir);
    setPage(1);
  };

  // One descriptor per column filter, rendered as a TableColumnHeader dropdown
  // on desktop and by MobileFilterBar below `lg`.
  const classifiedColumnFilter = {
    value: classifiedFilter,
    options: CLASSIFIED_FILTER_OPTIONS,
    onChange: (value) => {
      setClassifiedFilter(value);
      setPage(1);
    },
  };

  const musicbrainzColumnFilter = {
    value: musicbrainzFilter,
    options: YES_NO_FILTER_OPTIONS,
    onChange: (value) => {
      setMusicbrainzFilter(value);
      setPage(1);
    },
  };

  const socialColumnFilter = {
    value: socialFilter,
    options: YES_NO_FILTER_OPTIONS,
    onChange: (value) => {
      setSocialFilter(value);
      setPage(1);
    },
  };

  const domainColumnFilter = {
    value: domainFilter,
    options: [{ value: "", label: "All" }, ...domainOptions.map((d) => ({ value: d, label: d }))],
    onChange: (value) => {
      setDomainFilter(value);
      setPage(1);
    },
  };

  const cityColumnFilter = {
    value: cityFilter,
    options: [{ value: "", label: "All cities" }, ...cityOptions.map((c) => ({ value: c, label: capitalize(c) }))],
    onChange: (value) => {
      setCityFilter(value);
      setPage(1);
    },
  };

  const mobileColumnFilters = [
    { key: "domain", label: "Type", ...domainColumnFilter },
    { key: "city", label: "City", ...cityColumnFilter },
    { key: "classified", label: "Classified", ...classifiedColumnFilter },
    { key: "musicbrainz", label: "MusicBrainz", ...musicbrainzColumnFilter },
    { key: "social", label: "Social media", ...socialColumnFilter },
  ];

  const loadArtistDetails = async (artist) => {
    try {
      const data = await artistsApi.getById(artist.id);
      return normalizeArtist(data.artist || data);
    } catch {
      return normalizeArtist(artist);
    }
  };

  const handleClassify = async () => {
    const isReclassify = hasClassificationProfile(selectedArtist.classification_profile);
    if (
      isReclassify &&
      !window.confirm(
        "Re-classify this artist? The classifier's new result replaces the current classification (one LLM call) and is copied to every event that inherits it. If it fails, the current one is kept."
      )
    )
      return;
    const artist = selectedArtist;
    await classify.run(artist.id);
    fetchArtists({ silent: true });
    const refreshed = await loadArtistDetails(artist);
    setSelectedArtist((current) => (current?.id === artist.id ? refreshed : current));
  };

  // `isFullDetail`: skip the by-id refetch when the caller already holds the
  // full artist record (e.g. the deep-link effect, which fetches it up front).
  const handleOpenDetail = async (artist, isFullDetail = false) => {
    setArtistEvents([]);
    setArtistEventsLoading(true);
    setIsDetailModalOpen(true);
    const detail = isFullDetail ? artist : await loadArtistDetails(artist);
    setSelectedArtist(detail);
    try {
      const data = await eventsApi.getAll({ artist_id: detail.id, page_size: 100, sort_by: "date", sort_order: "desc" });
      setArtistEvents(data.events || []);
    } catch {
      setArtistEvents([]);
    } finally {
      setArtistEventsLoading(false);
    }
  };

  // Deep link: `?artist=<id>` (any EntityLink to it) opens that artist's
  // detail modal on load.
  useEntityDeepLink("artist", async (artistId) => {
    try {
      const data = await artistsApi.getById(artistId);
      // GET /admin/artists/{id} wraps the artist as { artist: {...} }
      handleOpenDetail(normalizeArtist(data?.artist ?? data), true);
    } catch (err) {
      console.error("Error opening artist from deep link:", err);
      setError(err.response?.data?.detail || "Could not open the requested artist");
    }
  });

  const handleOpenEdit = async (artist) => {
    const detail = await loadArtistDetails(artist);
    setSelectedArtist(detail);
    setEditData({
      sourceIds: objectToRows(detail.source_ids || {}, "source"),
      links: objectToRows(detail.links || {}, "link"),
      genres: genresToRows(detail.genres || []),
      preferred_name: detail.preferred_name || "",
      country: detail.country || "",
      begin_country: detail.begin_country || "",
      popularity: detail.popularity ?? "",
      domain: (detail.domain || []).join(", "),
      classificationProfile: classificationProfileToEdit(detail.classification_profile || {}),
      images: normalizeArtistImages(detail.images),
    });
    setGenreSearch("");
    setGenreResults([]);
    // Flag known-placeholder images so the editor can offer a bulk remove.
    checkSuspiciousImages(normalizeArtistImages(detail.images).map((i) => i.url));
    setIsEditModalOpen(true);
  };

  const handleGenreSearch = async (query) => {
    setGenreSearch(query);
    if (!query.trim()) {
      setGenreResults([]);
      return;
    }
    setGenreLoading(true);
    try {
      const data = await genresApi.getAll({ search: query.trim(), page_size: 10, has_embedding: true });
      setGenreResults(data.genres || []);
    } catch {
      setGenreResults([]);
    } finally {
      setGenreLoading(false);
    }
  };

  const handleAddGenre = (genre) => {
    setEditData((d) => {
      const current = d.genres || [];
      if (current.some((g) => g.id === genre.id)) return d;
      return {
        ...d,
        genres: [...current, { _key: genre.id, id: genre.id, name: genre.name || genre.id }],
      };
    });
    setGenreSearch("");
    setGenreResults([]);
  };

  const handleArtistImagesChange = (nextUrls) => {
    setEditData((d) => ({ ...d, images: reconcileImageUrls(d.images, nextUrls) }));
  };

  const handleSaveEdit = async () => {
    if (!selectedArtist) return;
    setIsSaving(true);
    setError(null);
    try {
      const updatePayload = {};

      const newSourceIds = rowsToObject(editData.sourceIds || []);
      if (JSON.stringify(newSourceIds) !== JSON.stringify(Object.fromEntries(definedEntries(selectedArtist.source_ids)))) {
        updatePayload.source_ids = Object.keys(newSourceIds).length > 0 ? newSourceIds : null;
      }
      const newLinks = rowsToObject(editData.links || []);
      if (JSON.stringify(newLinks) !== JSON.stringify(selectedArtist.links || {})) {
        updatePayload.links = Object.keys(newLinks).length > 0 ? newLinks : null;
      }
      const newPreferredName = (editData.preferred_name || "").trim();
      if (newPreferredName !== (selectedArtist.preferred_name || "")) {
        updatePayload.preferred_name = newPreferredName || null;
      }
      if (editData.country !== (selectedArtist.country || "")) {
        updatePayload.country = editData.country || null;
      }
      if (editData.begin_country !== (selectedArtist.begin_country || "")) {
        updatePayload.begin_country = editData.begin_country || null;
      }
      if (editData.popularity !== "" && editData.popularity !== (selectedArtist.popularity ?? "")) {
        const pop = parseFloat(editData.popularity);
        if (!isNaN(pop)) updatePayload.popularity = pop;
      }

      const newGenres = rowsToGenres(editData.genres || []);
      if (JSON.stringify(newGenres) !== JSON.stringify(genresToRows(selectedArtist.genres || []).map(({ id, name }) => ({ id, name })).filter((row) => row.id))) {
        updatePayload.genres = newGenres;
      }

      const newDomain = parseCommaSeparated(editData.domain);
      if (!arraysEqual(newDomain, selectedArtist.domain || [])) {
        updatePayload.domain = newDomain;
      }

      const originalClassificationProfile = selectedArtist.classification_profile || {};
      const editedClassification = editData.classificationProfile || {};
      const classificationChanges = changedClassificationFields(editedClassification, originalClassificationProfile);
      if (Object.keys(classificationChanges).length > 0) {
        // A new profile is sent whole; a stored one only where it changed.
        updatePayload.classification_profile = hasClassificationProfile(originalClassificationProfile)
          ? classificationChanges
          : buildClassificationProfile(editedClassification, originalClassificationProfile);
      }

      const newImages = (editData.images || []).filter((i) => i.url);
      if (JSON.stringify(newImages) !== JSON.stringify(normalizeArtistImages(selectedArtist.images))) {
        updatePayload.images = newImages;
      }

      if (Object.keys(updatePayload).length === 0) {
        setIsEditModalOpen(false);
        return;
      }

      const result = await artistsApi.update(selectedArtist.id, updatePayload);
      setSuccess(`Artist updated: ${result.updated_fields?.join(", ") || "OK"}`);
      setIsEditModalOpen(false);
      setSelectedArtist(null);
      fetchArtists();
    } catch (err) {
      console.error("Error updating artist:", err);
      setError(err.response?.data?.detail || "Error updating artist");
    } finally {
      setIsSaving(false);
    }
  };

  const handleOpenDelete = (artist) => {
    setSelectedArtist(artist);
    setDeleteImpact(null);
    setIsDeleteModalOpen(true);
  };

  // Two clicks, and it is the server that decides there must be two: the first call sends no
  // `confirm`, so it writes nothing and comes back 409 with the impact whenever an event or
  // profile still references the artist. That answer is what fills the dialog. The second
  // click repeats the call with confirm and goes through. An unreferenced artist has nothing
  // to preview, so its first click deletes.
  const handleDelete = async () => {
    if (!selectedArtist) return;
    const confirm = deleteImpact !== null;
    setIsDeleting(true);
    setError(null);
    try {
      // The response is the only place an incomplete cascade is reported — a rejected bulk
      // write leaves refs behind without failing the call — so use its message rather than
      // assuming success, and route anything but a verified-clean result to the warning banner.
      const result = await artistsApi.delete(selectedArtist.id, { confirm });
      if (artistDeleteNeedsAttention(result)) {
        setError(result.message);
      } else {
        setSuccess(result?.message || "Artist deleted successfully");
      }
      setIsDeleteModalOpen(false);
      setSelectedArtist(null);
      setDeleteImpact(null);
      fetchArtists();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409 && detail?.events_total !== undefined) {
        setDeleteImpact(detail);
      } else {
        console.error("Error deleting artist:", err);
        setError(typeof detail === "string" ? detail : "Error deleting artist");
      }
    } finally {
      setIsDeleting(false);
    }
  };

  const handleOpenChangeMbid = async (artistOverride = null) => {
    // When opened from the duplicates view we pass the (no-MBID) artist explicitly; otherwise
    // fall back to the currently selected artist from the table/detail modal.
    const targetArtist = artistOverride && artistOverride.id ? artistOverride : selectedArtist;
    if (artistOverride && artistOverride.id) {
      setSelectedArtist(artistOverride);
    }
    setMbidSearchQuery("");
    setMbidSearchResults([]);
    setMbidSearchError(null);
    resetMbidSelection();
    setMbidChecking(false);
    setMbidResultsSource(null);
    setMbidSearchLimit(MBID_SEARCH_PAGE);
    setIsMbidModalOpen(true);

    const spotifyId = targetArtist?.source_ids?.spotify;
    const youtubeUrl = targetArtist?.links?.youtube;
    const artistName = targetArtist?.name;

    setMbidSearchLoading(true);
    try {
      const [spotifyResults, youtubeResults] = await Promise.allSettled([
        spotifyId ? mbSearchBySpotify(spotifyId) : Promise.resolve([]),
        youtubeUrl ? mbSearchByYoutube(youtubeUrl) : Promise.resolve([]),
      ]);

      const seen = new Set();
      const merged = [];
      for (const r of [spotifyResults, youtubeResults]) {
        if (r.status === "fulfilled") {
          for (const artist of r.value) {
            if (!seen.has(artist.id)) {
              seen.add(artist.id);
              merged.push(artist);
            }
          }
        }
      }

      if (merged.length > 0) {
        setMbidSearchResults(merged);
        const hasSpotify = spotifyResults.status === "fulfilled" && spotifyResults.value.length > 0;
        const hasYoutube = youtubeResults.status === "fulfilled" && youtubeResults.value.length > 0;
        if (hasSpotify && hasYoutube) setMbidResultsSource("spotify+youtube");
        else if (hasSpotify) setMbidResultsSource("spotify");
        else setMbidResultsSource("youtube");
      } else if (artistName) {
        const nameResults = await mbSearchArtists(artistName, MBID_SEARCH_PAGE);
        if (nameResults.length > 0) {
          setMbidSearchResults(nameResults);
          setMbidResultsSource("name");
          setMbidSearchQuery(artistName);
        }
      }
    } catch {
      setMbidSearchError("Could not fetch suggestions — search by name above");
    } finally {
      setMbidSearchLoading(false);
    }
  };

  useEffect(() => {
    if (!isMbidModalOpen) return;
    if (mbidDebounceRef.current) clearTimeout(mbidDebounceRef.current);
    const q = mbidSearchQuery.trim();
    if (q.length < 2) {
      // Always clear stale name-search results when input is too short.
      // Pre-loaded Spotify/YouTube results are set after this effect fires on
      // initial open, so clearing here is a no-op at that point.
      setMbidSearchResults([]);
      setMbidResultsSource(null);
      setMbidSearchLoading(false);
      return;
    }
    setMbidSearchLoading(true);
    setMbidSearchError(null);
    mbidDebounceRef.current = setTimeout(async () => {
      try {
        const results = await mbSearchArtists(q, mbidSearchLimit);
        setMbidSearchResults(results);
        setMbidResultsSource("name");
      } catch (err) {
        console.error("MusicBrainz search failed", err);
        setMbidSearchError("Search failed — check connectivity and try again");
        setMbidSearchResults([]);
        setMbidResultsSource(null);
      } finally {
        setMbidSearchLoading(false);
      }
    }, 600);
    return () => mbidDebounceRef.current && clearTimeout(mbidDebounceRef.current);
  }, [mbidSearchQuery, mbidSearchLimit, isMbidModalOpen]);

  const handleSelectMbCandidate = async (rawArtist) => {
    const formatted = mbFormatArtist(rawArtist);
    setMbidSelected(formatted);
    setMbidSearchResults([]);
    setMbidSearchQuery("");
    setMbidCheck(null);
    setMbidChecking(true);
    try {
      const result = await artistsApi.checkMusicbrainzId(formatted.mbid, selectedArtist?.id);
      setMbidCheck(result);
    } catch (err) {
      console.error("Failed to check MusicBrainz ID", err);
      setMbidCheck({ available: false, conflicting_artists: [], _error: "Failed to verify availability" });
    } finally {
      setMbidChecking(false);
    }
  };

  const handleSaveMbid = async () => {
    if (!selectedArtist || !mbidSelected || !mbidCheck?.available) return;
    setMbidSaving(true);
    setError(null);
    try {
      const result = await artistsApi.changeMusicbrainzId(selectedArtist.id, mbidSelected.mbid, mbidKeepClassification === true);
      setSuccess(
        result.merged
          ? `MusicBrainz ID matched an existing artist — the two were merged automatically.`
          : `MusicBrainz ID updated. Fields refreshed: ${
              result.updated_fields?.join(", ") || "none"
            }. Synced events: ${result.embedding_sync?.event_sync?.[1] ?? 0}, users: ${
              result.embedding_sync?.user_sync?.[1] ?? 0
            }.`
      );
      setIsMbidModalOpen(false);
      resetMbidSelection();
      // Keep the duplicate-review queue + badge in sync (this MBID change may have resolved or
      // auto-merged a flagged pair — e.g. when invoked from the duplicates tab's "mark different").
      refreshDuplicateQueue(true);
      const refreshed = await loadArtistDetails(selectedArtist);
      setSelectedArtist(refreshed);
    } catch (err) {
      console.error("Failed to change MusicBrainz ID", err);
      if (err.response?.status === 409) {
        const detail = err.response?.data?.detail;
        setMbidCheck({
          available: false,
          conflicting_artists: detail?.conflicting_artists || [],
          _error: detail?.message || "MusicBrainz ID already used by another artist",
        });
      } else {
        setError(err.response?.data?.detail?.message || err.response?.data?.detail || "Failed to change MusicBrainz ID");
      }
    } finally {
      setMbidSaving(false);
    }
  };

  // Both manual merge paths end the same way: the source artist is gone from the list, and the
  // merge closed every open review pair referencing it, so the queue + badge need a reload too.
  const afterArtistMerge = (message) => {
    setSuccess(message);
    setIsMarkDuplicateOpen(false);
    setIsMbidModalOpen(false);
    resetMbidSelection();
    closeDetailModal();
    refreshDuplicateQueue(true);
  };

  const handleOpenMarkDuplicate = () => {
    setDupPicked(null);
    setDupError(null);
    setIsMarkDuplicateOpen(true);
  };

  const handleMarkDuplicate = async () => {
    if (!selectedArtist || !dupPicked) return;
    setDupBusy(true);
    setDupError(null);
    try {
      // The picked artist is folded in; the open artist stays canonical.
      await artistsApi.mergeInto(dupPicked.id, selectedArtist.id);
      afterArtistMerge(`"${dupPicked.label || dupPicked.id}" merged into "${displayLabel(selectedArtist.preferred_name, selectedArtist.name)}"`);
    } catch (err) {
      setDupError(mergeErrorMessage(err));
    } finally {
      setDupBusy(false);
    }
  };

  // The MBID conflict case is the mirror image: the artist that already holds the MBID is the
  // canonical, and the artist we were about to assign it to is the duplicate folded into it.
  const handleMergeIntoConflict = async (conflict) => {
    if (!selectedArtist || !conflict?.id) return;
    setDupBusy(true);
    try {
      await artistsApi.mergeInto(selectedArtist.id, conflict.id);
      afterArtistMerge(`"${displayLabel(selectedArtist.preferred_name, selectedArtist.name)}" merged into "${conflict.name || conflict.id}"`);
    } catch (err) {
      // The MBID modal is on top of the page banner, so the failure has to land inside it.
      setMbidCheck((check) => ({ ...(check || {}), _error: mergeErrorMessage(err) }));
    } finally {
      setDupBusy(false);
    }
  };

  const totalPages = pagination?.total_pages || 1;
  const totalArtists = pagination?.total || 0;

  return (
    <div className="space-y-6">
      {/* Top-left tabs: Artists table vs. duplicate review queue */}
      <div className="flex items-center gap-2 border-b border-border">
        <button
          onClick={() => setActiveTab("artists")}
          className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors ${
            activeTab === "artists"
              ? "border-primary text-foreground"
              : "border-transparent text-muted-foreground hover:text-foreground"
          }`}
        >
          Artists
        </button>
        <button
          onClick={() => setActiveTab("duplicates")}
          className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors ${
            activeTab === "duplicates"
              ? "border-primary text-foreground"
              : artistDuplicateCount > 0
              ? "border-transparent text-yellow-700 hover:text-yellow-800"
              : "border-transparent text-muted-foreground hover:text-foreground"
          }`}
        >
          Duplicates ({artistDuplicateCount})
        </button>
        <button
          onClick={() => setActiveTab("deleted")}
          className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors ${
            activeTab === "deleted"
              ? "border-primary text-foreground"
              : "border-transparent text-muted-foreground hover:text-foreground"
          }`}
        >
          Deleted
        </button>
      </div>

      {activeTab === "duplicates" && (
        <ArtistDuplicatesView refreshSignal={dupRefreshSignal} onQueueChange={setArtistDuplicateCount} />
      )}

      {activeTab === "deleted" && <DeletedArtistsView />}

      {activeTab === "artists" && (<>
      {/* Search */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <SearchFilterForm
          value={searchInput}
          onChange={setSearchInput}
          onSubmit={handleSearch}
          onClear={handleClearSearch}
          placeholder="Search artists by name or ID"
          inputClassName="sm:w-64"
        >
          <input
            type="text"
            placeholder="Country (ES, US...)"
            value={countryInput}
            onChange={(e) => setCountryInput(e.target.value)}
            maxLength={2}
            className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm uppercase focus:outline-none focus:ring-2 focus:ring-ring sm:w-32"
          />
        </SearchFilterForm>
        <Button type="button" variant="outline" onClick={fetchArtists} disabled={loading}>
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
        </Button>
      </div>

      {/* Messages */}
      {error && (
        <div className="p-3 rounded-md text-sm bg-red-500/10 text-red-700 border border-red-500/20">
          {error}
          <button className="ml-2 underline" onClick={() => setError(null)}>Close</button>
        </div>
      )}
      {success && (
        <div className="p-3 rounded-md text-sm bg-green-500/10 text-green-700 border border-green-500/20">
          {success}
          <button className="ml-2 underline" onClick={() => setSuccess(null)}>Close</button>
        </div>
      )}

      {/* Header + Stats */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="text-2xl font-bold tracking-tight">Artists</h2>
        <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
          <span className="font-medium text-foreground">{totalArtists} artists</span>
          {searchFilter && <Badge variant="outline">"{searchFilter}"</Badge>}
          {countryFilter && <Badge variant="secondary">{countryFilter}</Badge>}
        </div>
      </div>

      {/* The city filter changes what the Upcoming column means, not just which rows show, so
          it says so in words — a tinted filter icon in a header is easy to walk past. */}
      {cityFilter && (
        <div className="flex flex-wrap items-center gap-2 rounded-md border border-border bg-muted/40 p-3 text-sm">
          <MapPin className="h-4 w-4 shrink-0 text-muted-foreground" />
          <span>
            Showing artists with upcoming events in <span className="font-medium">{capitalize(cityFilter)}</span>.
            Upcoming counts are for {capitalize(cityFilter)} only.
          </span>
          <button
            type="button"
            className="underline text-muted-foreground hover:text-foreground"
            onClick={() => cityColumnFilter.onChange("")}
          >
            Clear
          </button>
        </div>
      )}

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              <span className="ml-2 text-muted-foreground">Loading artists...</span>
            </div>
          ) : (
            <>
              {/* Mobile filter/sort bar */}
              <div className="border-b border-border p-3 space-y-2.5 lg:hidden">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-muted-foreground shrink-0">Sort</span>
                  {ARTIST_SORT_COLUMNS.map((column) => (
                    <button
                      key={column.key}
                      type="button"
                      onClick={() => handleSort(column.key)}
                      className={chip(sortKey === column.key)}
                    >
                      {column.label}
                      {sortKey === column.key ? (
                        sortDir === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                      ) : (
                        <ArrowUpDown className="h-3 w-3 opacity-50" />
                      )}
                    </button>
                  ))}
                </div>
                <MobileFilterBar filters={mobileColumnFilters} />
              </div>

              <div className={cn("divide-y divide-border lg:hidden", backgroundLoading && "opacity-60")}>
                {artists.length === 0 && (
                  <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                    <Database className="h-12 w-12 mb-4" />
                    <p className="text-lg font-medium">No artists found</p>
                  </div>
                )}
                {artists.map((artist) => (
                  <div
                    key={artist.id}
                    className="space-y-3 p-4"
                    onClick={() => handleOpenDetail(artist)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === "Enter" && handleOpenDetail(artist)}
                  >
                    <div className="flex items-start gap-3">
                      <div className="h-11 w-11 shrink-0 rounded-full bg-primary/10 flex items-center justify-center">
                        {React.createElement(domainIcon(artist.domain), { className: "h-5 w-5 text-primary" })}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-medium">{displayLabel(artist.preferred_name, artist.name)}</p>
                        <ProviderLabel preferred={artist.preferred_name} original={artist.name} />
                        <div className="mt-1 flex flex-wrap gap-1">
                          {(artist.domain || []).map((d) => (
                            <Badge key={d} variant="outline" className="text-xs">{d}</Badge>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-1">
                      {(artist.genres || []).slice(0, 4).map((g) => (
                        <Badge key={g.id || g.name} variant="secondary" className="text-xs">
                          {g.name}
                        </Badge>
                      ))}
                      {(artist.genres || []).length > 4 && (
                        <span className="text-xs text-muted-foreground">+{artist.genres.length - 4}</span>
                      )}
                      {(!artist.genres || artist.genres.length === 0) && (
                        <span className="text-xs text-muted-foreground">No genres</span>
                      )}
                    </div>

                    <div className="grid gap-3 text-sm sm:grid-cols-2">
                      <div>
                        <p className="text-xs text-muted-foreground">
                          Upcoming events{cityFilter ? ` in ${capitalize(cityFilter)}` : ""}
                        </p>
                        <p className="mt-1">{artist.upcoming_event_count ?? "-"}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Favorited by</p>
                        <p className="mt-1">{artist.user_count ?? "-"}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Country</p>
                        <p className="mt-1 uppercase">{artist.country || "-"}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Popularity</p>
                        <p className="mt-1">{artist.popularity ?? "-"}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Classified</p>
                        <div className="mt-1">
                          <ClassificationIssueBadge profile={artist.classification_profile} meta={artist.enrichment_metadata} enricher="artist_classification" labels={["Yes", "No"]} />
                        </div>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">MusicBrainz</p>
                        <div className="mt-1">
                          {artist.source_ids?.musicbrainz ? (
                            <Badge variant="secondary" className="text-xs">Yes</Badge>
                          ) : (
                            <Badge variant="outline" className="text-xs text-muted-foreground">No</Badge>
                          )}
                        </div>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Social media</p>
                        <div className="mt-1">
                          {hasSocialLinks(artist.links) ? (
                            <Badge variant="secondary" className="text-xs">Yes</Badge>
                          ) : (
                            <Badge variant="outline" className="text-xs text-muted-foreground">No</Badge>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center justify-between gap-3">
                      <span className="text-xs text-muted-foreground">
                        First seen {formatDate(artist.first_seen)}
                      </span>
                      <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="icon" onClick={() => handleOpenEdit(artist)} title="Edit">
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleOpenDelete(artist)} title="Delete">
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="hidden lg:block">
                <Table className={backgroundLoading ? "opacity-60" : ""}>
              <TableHeader>
                <TableRow>
                  <TableHead>Artist</TableHead>
                  <TableColumnHeader label="Type" filter={domainColumnFilter} />
                  {/* The city filter lives on this column because it scopes the number in it:
                      an act playing Madrid and Paris reads 1 under a Madrid filter, not 2. */}
                  <TableColumnHeader
                    label={SORT_COLUMN.upcoming_event_count.label}
                    sortDir={sortKey === "upcoming_event_count" ? sortDir : null}
                    onSort={() => handleSort("upcoming_event_count")}
                    filter={cityColumnFilter}
                    filterLabel="city"
                  />
                  <TableColumnHeader
                    label={SORT_COLUMN.user_count.label}
                    sortDir={sortKey === "user_count" ? sortDir : null}
                    onSort={() => handleSort("user_count")}
                  />
                  <TableHead>Genres</TableHead>
                  <TableHead>Country</TableHead>
                  <TableHead>Popularity</TableHead>
                  <TableColumnHeader label="Classified?" filter={classifiedColumnFilter} />
                  <TableHead>External IDs</TableHead>
                  <TableColumnHeader label="MusicBrainz?" filter={musicbrainzColumnFilter} />
                  <TableColumnHeader label="Social media?" filter={socialColumnFilter} />
                  <TableColumnHeader
                    label={SORT_COLUMN.first_seen.label}
                    sortDir={sortKey === "first_seen" ? sortDir : null}
                    onSort={() => handleSort("first_seen")}
                  />
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {artists.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={13}>
                      <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                        <Database className="h-12 w-12 mb-4" />
                        <p className="text-lg font-medium">No artists found</p>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
                {artists.map((artist) => (
                  <TableRow
                    key={artist.id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => handleOpenDetail(artist)}
                  >
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                          {React.createElement(domainIcon(artist.domain), { className: "h-5 w-5 text-primary" })}
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium truncate max-w-[200px]">{displayLabel(artist.preferred_name, artist.name)}</p>
                          <ProviderLabel preferred={artist.preferred_name} original={artist.name} className="truncate max-w-[200px]" />
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1 max-w-[140px]">
                        {(artist.domain || []).map((d) => (
                          <Badge key={d} variant="outline" className="text-xs">{d}</Badge>
                        ))}
                        {(artist.domain || []).length === 0 && (
                          <span className="text-xs text-muted-foreground">-</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm font-medium">{artist.upcoming_event_count ?? "-"}</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm font-medium">{artist.user_count ?? "-"}</span>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1 max-w-[200px]">
                        {(artist.genres || []).slice(0, 3).map((g) => (
                          <Badge key={g.id || g.name} variant="secondary" className="text-xs">
                            {g.name}
                          </Badge>
                        ))}
                        {(artist.genres || []).length > 3 && (
                          <span className="text-xs text-muted-foreground">
                            +{artist.genres.length - 3}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {artist.country ? (
                        <div className="flex items-center gap-1">
                          <Globe className="h-3 w-3 text-muted-foreground" />
                          <span className="text-sm uppercase">{artist.country}</span>
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {artist.popularity != null ? (
                        <div className="flex items-center gap-2">
                          <div className="w-16 h-2 rounded-full bg-muted overflow-hidden">
                            <div
                              className="h-full bg-primary rounded-full"
                              style={{ width: `${artist.popularity}%` }}
                            />
                          </div>
                          <span className="text-xs text-muted-foreground">{artist.popularity}</span>
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <ClassificationIssueBadge profile={artist.classification_profile} meta={artist.enrichment_metadata} enricher="artist_classification" labels={["Yes", "No"]} />
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <div className="flex flex-wrap gap-1">
                        {definedEntries(artist.source_ids).map(([provider]) => (
                          <Badge key={provider} variant="outline" className="text-xs">{provider}</Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell>
                      {artist.source_ids?.musicbrainz ? (
                        <Badge variant="secondary" className="text-xs">Yes</Badge>
                      ) : (
                        <Badge variant="outline" className="text-xs text-muted-foreground">No</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {hasSocialLinks(artist.links) ? (
                        <Badge variant="secondary" className="text-xs">Yes</Badge>
                      ) : (
                        <Badge variant="outline" className="text-xs text-muted-foreground">No</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className="text-xs text-muted-foreground whitespace-nowrap">
                        {formatDate(artist.first_seen)}
                      </span>
                    </TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="icon" onClick={() => handleOpenEdit(artist)} title="Edit">
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleOpenDelete(artist)} title="Delete">
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
                </Table>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
      </>)}

      {/* Detail Modal */}
      <Modal open={isDetailModalOpen} onOpenChange={(open) => (open ? setIsDetailModalOpen(true) : closeDetailModal())}>
        <ModalContent title="Artist Details" className="max-w-2xl max-h-[calc(100dvh-1rem)] overflow-y-auto sm:max-h-[90vh]">
          {selectedArtist && (
            <div className="space-y-4">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
                <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
                  {React.createElement(domainIcon(selectedArtist.domain), { className: "h-8 w-8 text-primary" })}
                </div>
                <div>
                  <h3 className="text-lg font-semibold">{selectedArtist.name}</h3>
                  <PreferredLabel label="Preferred name" value={selectedArtist.preferred_name} original={selectedArtist.name} />
                  <div className="flex flex-wrap gap-1 mt-1">
                    {(selectedArtist.domain || []).map((d) => (
                      <Badge key={d} variant="outline">{d}</Badge>
                    ))}
                    {selectedArtist.classification_profile?.classification_source && (
                      <Badge variant="secondary">{capitalize(selectedArtist.classification_profile.classification_source)}</Badge>
                    )}
                  </div>
                </div>
              </div>

              <div className="grid gap-4 text-sm sm:grid-cols-2">
                <div>
                  <p className="text-muted-foreground">ID</p>
                  <p className="font-mono text-xs truncate">{selectedArtist.id}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Country</p>
                  <p>{selectedArtist.country || "-"}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Popularity</p>
                  <p>{selectedArtist.popularity ?? "-"}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Country of origin</p>
                  <p>{selectedArtist.begin_country || "-"}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Source IDs</p>
                  <div className="space-y-1">
                    {definedEntries(selectedArtist.source_ids).map(([key, value]) => (
                      <p key={key} className="font-mono text-xs truncate">{key}: {value}</p>
                    ))}
                    {definedEntries(selectedArtist.source_ids).length === 0 && <p>-</p>}
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="mt-2"
                    onClick={handleOpenChangeMbid}
                  >
                    {selectedArtist.source_ids?.musicbrainz ? "Change MusicBrainz ID" : "Set MusicBrainz ID"}
                  </Button>
                </div>
                <div>
                  <p className="text-muted-foreground">Links</p>
                  <div className="space-y-1">
                    {Object.entries(selectedArtist.links || {}).map(([key, value]) => (
                      <a key={key} href={value} target="_blank" rel="noopener noreferrer" className="block truncate text-xs text-blue-600 hover:underline">
                        {key}: {value}
                      </a>
                    ))}
                    {Object.keys(selectedArtist.links || {}).length === 0 && <p>-</p>}
                  </div>
                </div>
              </div>

              <ClassifyAction
                busy={classify.isBusy(selectedArtist.id)}
                result={classify.resultFor(selectedArtist.id)}
                isReclassify={hasClassificationProfile(selectedArtist.classification_profile)}
                onClick={handleClassify}
                renderPrevious={(r) =>
                  r.previous_classification?.classification_profile && (
                    <ClassificationProfileDetail profile={r.previous_classification.classification_profile} defaultSource="artist" />
                  )
                }
              >
                <ClassificationIssueBadge
                  profile={selectedArtist.classification_profile}
                  meta={selectedArtist.enrichment_metadata}
                  enricher="artist_classification"
                />
              </ClassifyAction>

              <ClassificationProfileDetail profile={selectedArtist.classification_profile} defaultSource="artist" />

              <GenreChips title="Genres" genres={selectedArtist.genres} variant="secondary" empty="No genres" />

              {/* The classifier's picks (#718), stored beside the provider's genres. */}
              <GenreChips
                title="Curated genres"
                genres={selectedArtist.curated_genres}
                variant="outline"
                empty={selectedArtist.curated_genres_source ? "Asked, none fit" : "Not curated yet"}
              >
                {selectedArtist.curated_genres_source && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {capitalize(selectedArtist.curated_genres_source.source || "")} ·{" "}
                    {selectedArtist.curated_genres_source.model} · prompt v
                    {selectedArtist.curated_genres_source.prompt_version} ·{" "}
                    {formatDate(selectedArtist.curated_genres_source.at)}
                  </p>
                )}
              </GenreChips>

              {normalizeArtistImages(selectedArtist?.images).length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Images</h4>
                  <div className="grid gap-3 sm:grid-cols-3">
                    {normalizeArtistImages(selectedArtist?.images).map((img, idx) => (
                      <div key={idx} className="relative rounded-lg overflow-hidden border border-border bg-muted/30">
                        <a href={img.url} target="_blank" rel="noreferrer" title="Open image in a new tab">
                          <img src={img.url} alt={`Image ${idx + 1}`} className="w-full aspect-video object-cover cursor-zoom-in" />
                        </a>
                        {img.source && (
                          <Badge variant="secondary" className="absolute bottom-1.5 left-1.5 text-[10px]">
                            {img.source}
                          </Badge>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selectedArtist.user_count != null && (
                <div className="text-sm">
                  <p className="text-muted-foreground">Associated users</p>
                  <p className="text-lg font-bold">{selectedArtist.user_count}</p>
                </div>
              )}

              {/* Events list */}
              <div>
                <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                  Events
                  {selectedArtist.event_count != null && (
                    <span className="ml-2 font-normal normal-case">({selectedArtist.event_count})</span>
                  )}
                </p>
                {artistEventsLoading ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading events…
                  </div>
                ) : artistEvents.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No events found</p>
                ) : (
                  <ul className="space-y-1.5 max-h-64 overflow-y-auto pr-1 text-sm">
                    {artistEvents.map((ev) => (
                      <ReviewEventLink key={ev._id || ev.id} event={summarizeArtistEvent(ev)} />
                    ))}
                    {/* Same set on both sides: `event_count` and the list query both match every
                        referencing event with no status or date filter, so the difference is
                        exactly what this page left out. */}
                    {selectedArtist.event_count > artistEvents.length && (
                      <li className="text-[10px] italic text-muted-foreground">
                        +{selectedArtist.event_count - artistEvents.length} more not shown
                      </li>
                    )}
                  </ul>
                )}
              </div>

              <ModalFooter className="border-t pt-4">
                <Button variant="outline" size="sm" onClick={() => {
                  setIsDetailModalOpen(false);
                  handleOpenEdit(selectedArtist);
                }}>
                  <Pencil className="h-4 w-4 mr-2" /> Edit
                </Button>
                {/* A duplicate can't be a canonical: the merge would hop to *its* canonical, which
                    is not the artist on screen. Only offer the action where it means what it says. */}
                {selectedArtist.status !== "duplicate" && (
                  <Button variant="outline" size="sm" onClick={handleOpenMarkDuplicate}>
                    <GitMerge className="h-4 w-4 mr-2" /> Add duplicate
                  </Button>
                )}
                <Button variant="outline" onClick={closeDetailModal}>
                  Close
                </Button>
              </ModalFooter>
            </div>
          )}
        </ModalContent>
      </Modal>

      {/* Manual duplicate: fold another artist into the open (canonical) one */}
      <Modal open={isMarkDuplicateOpen} onOpenChange={(open) => { if (!dupBusy) setIsMarkDuplicateOpen(open); }}>
        <ModalContent title="Add duplicate" className="max-w-lg">
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Canonical: <span className="font-medium text-foreground">{displayLabel(selectedArtist?.preferred_name, selectedArtist?.name)}</span>.
              The artist you pick is merged into it — {MERGE_EFFECT_COPY}
            </p>

            <EntitySearchPicker
              id="mark_duplicate_artist_search"
              label="Duplicate artist"
              required
              placeholder="Search artists by name, or paste an artist ID..."
              value={dupPicked}
              onChange={setDupPicked}
              disabled={dupBusy}
              excludeId={selectedArtist?.id}
              allowRawId={false}
              emptyLabel="No artist matches that name or ID."
              search={(query, config) =>
                // The admin artist list already excludes status=duplicate, so no artist that
                // can't be a merge source can be picked here.
                artistsApi.getAll({ search: query, page_size: 8 }, config).then((data) => data.artists || [])
              }
              optionLabel={(artist) => displayLabel(artist.preferred_name, artist.name)}
              optionSublabel={(artist) =>
                [artist.country, artist.source_ids?.musicbrainz || artist.musicbrainz_id ? "MBID" : null]
                  .filter(Boolean)
                  .join(" · ")
              }
            />

            {dupError && <p className="text-sm text-destructive">{dupError}</p>}
          </div>

          <ModalFooter className="mt-4">
            <Button variant="outline" onClick={() => setIsMarkDuplicateOpen(false)} disabled={dupBusy}>
              Cancel
            </Button>
            <MergeIntoButton onClick={handleMarkDuplicate} disabled={!dupPicked || dupBusy} busy={dupBusy} />
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Edit Modal */}
      <Modal open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <ModalContent title="Edit Artist" className="max-w-4xl max-h-[calc(100dvh-1rem)] overflow-y-auto sm:max-h-[90vh]">
          {selectedArtist && (
            <div className="space-y-6">
              <div className="text-sm text-muted-foreground mb-2">
                <strong>{selectedArtist.name}</strong> (name not editable)
              </div>

              <div>
                <label className="text-sm font-medium">Preferred name</label>
                <input
                  type="text"
                  value={editData.preferred_name || ""}
                  onChange={(e) => setEditData((d) => ({ ...d, preferred_name: e.target.value }))}
                  maxLength={300}
                  placeholder={selectedArtist.name}
                  className="mt-1 w-full h-10 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                />
                <p className="mt-1 text-xs text-muted-foreground">
                  Used for duplicate matching instead of the provider&apos;s name. Strip billing additions
                  here (e.g. &ldquo;Brothers In Band&rdquo; for &ldquo;Brothers In Band: Dire Straits Tribute&rdquo;).
                  Leave empty to match on the name above.
                </p>
              </div>

              <div className="space-y-3">
                <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">External IDs</h4>
                {(editData.sourceIds || []).map((row, idx) => (
                  <div key={row._key ?? idx} className="grid grid-cols-1 gap-2 sm:grid-cols-[10rem_minmax(0,1fr)_2.25rem]">
                    <input type="text" value={row.key || ""} onChange={(e) => {
                      const updated = [...editData.sourceIds];
                      updated[idx] = { ...updated[idx], key: e.target.value };
                      setEditData((d) => ({ ...d, sourceIds: updated }));
                    }} placeholder="spotify" className="h-9 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
                    <input type="text" value={row.value || ""} onChange={(e) => {
                      const updated = [...editData.sourceIds];
                      updated[idx] = { ...updated[idx], value: e.target.value };
                      setEditData((d) => ({ ...d, sourceIds: updated }));
                    }} placeholder="Provider ID" className="h-9 min-w-0 rounded-md border border-input bg-background px-3 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-ring" />
                    <Button type="button" variant="ghost" size="icon" className="h-9 w-9" onClick={() => setEditData((d) => ({ ...d, sourceIds: d.sourceIds.filter((_, i) => i !== idx) }))}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                <Button type="button" variant="outline" size="sm" onClick={() => setEditData((d) => ({ ...d, sourceIds: [...(d.sourceIds || []), { _key: `source-${Date.now()}`, key: "", value: "" }] }))}>
                  Add source ID
                </Button>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="text-sm font-medium">Country (ISO 2)</label>
                  <input
                    type="text"
                    value={editData.country}
                    onChange={(e) => setEditData((d) => ({ ...d, country: e.target.value }))}
                    maxLength={2}
                    className="mt-1 w-full h-10 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring uppercase"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Origin country (ISO 2)</label>
                  <input
                    type="text"
                    value={editData.begin_country}
                    onChange={(e) => setEditData((d) => ({ ...d, begin_country: e.target.value }))}
                    maxLength={2}
                    className="mt-1 w-full h-10 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring uppercase"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Popularity (0-100)</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={editData.popularity}
                    onChange={(e) => setEditData((d) => ({ ...d, popularity: e.target.value }))}
                    className="mt-1 w-full h-10 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </div>
              </div>

              <div className="space-y-3">
                <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Links</h4>
                {(editData.links || []).map((row, idx) => (
                  <div key={row._key ?? idx} className="grid grid-cols-1 gap-2 sm:grid-cols-[10rem_minmax(0,1fr)_2.25rem]">
                    <input type="text" value={row.key || ""} onChange={(e) => {
                      const updated = [...editData.links];
                      updated[idx] = { ...updated[idx], key: e.target.value };
                      setEditData((d) => ({ ...d, links: updated }));
                    }} placeholder="spotify" className="h-9 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
                    <input type="url" value={row.value || ""} onChange={(e) => {
                      const updated = [...editData.links];
                      updated[idx] = { ...updated[idx], value: e.target.value };
                      setEditData((d) => ({ ...d, links: updated }));
                    }} placeholder="https://..." className="h-9 min-w-0 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
                    <Button type="button" variant="ghost" size="icon" className="h-9 w-9" onClick={() => setEditData((d) => ({ ...d, links: d.links.filter((_, i) => i !== idx) }))}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                <Button type="button" variant="outline" size="sm" onClick={() => setEditData((d) => ({ ...d, links: [...(d.links || []), { _key: `link-${Date.now()}`, key: "", value: "" }] }))}>
                  Add link
                </Button>
              </div>

              <ImageListEditor
                value={(editData.images || []).map((i) => i.url)}
                onChange={handleArtistImagesChange}
                suspiciousUrls={editSuspiciousImages}
                renderBadge={(url) => {
                  const src = (editData.images || []).find((i) => i.url === url)?.source;
                  return src ? (
                    <Badge variant="secondary" className="text-[10px]">
                      {src}
                    </Badge>
                  ) : null;
                }}
              />

              <div className="space-y-3">
                <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Genres</h4>
                <div className="flex flex-wrap gap-1">
                  {(editData.genres || []).map((row) => (
                    <button
                      key={row._key || row.id || row.name}
                      type="button"
                      onClick={() => setEditData((d) => ({ ...d, genres: (d.genres || []).filter((g) => g.id !== row.id) }))}
                      className={GENRE_CHIP_CLASS}
                      title="Remove genre"
                    >
                      {row.name || row.id}
                    </button>
                  ))}
                  {(!editData.genres || editData.genres.length === 0) && (
                    <span className="text-sm text-muted-foreground">No genres</span>
                  )}
                </div>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <input
                    type="text"
                    value={genreSearch}
                    onChange={(e) => handleGenreSearch(e.target.value)}
                    placeholder="Search existing genres..."
                    className="w-full h-10 rounded-md border border-input bg-background pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                  {(genreResults.length > 0 || genreLoading) && (
                    <div className="absolute z-50 mt-1 w-full rounded-md border border-input bg-background shadow-lg">
                      {genreLoading ? (
                        <div className="flex items-center justify-center py-3">
                          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                          <span className="ml-2 text-sm text-muted-foreground">Searching...</span>
                        </div>
                      ) : (
                        genreResults.map((genre) => (
                          <button
                            key={genre.id}
                            type="button"
                            className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-muted/50"
                            onClick={() => handleAddGenre(genre)}
                          >
                            <span className="font-medium">{genre.name}</span>
                            <span className="font-mono text-xs text-muted-foreground">{genre.id}</span>
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className="text-sm font-medium">Domain</label>
                <input
                  type="text"
                  value={editData.domain}
                  onChange={(e) => setEditData((d) => ({ ...d, domain: e.target.value }))}
                  placeholder="music, comedy..."
                  className="mt-1 w-full h-10 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                />
                <p className="text-xs text-muted-foreground mt-1">Comma separated</p>
              </div>

              <div className="space-y-3">
                <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Classification</h4>
                <div className="space-y-3 rounded-md bg-muted/30 p-3">
                  <div className="grid gap-4 sm:grid-cols-3">
                    <div>
                      <label className="text-sm font-medium">Source</label>
                      <div className="mt-1 flex h-10 items-center rounded-md border border-input bg-muted px-3 text-sm text-muted-foreground">
                        {capitalize(editData.classificationProfile?.classification_source || "artist")}
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium">Primary type</label>
                      <select value={editData.classificationProfile?.primary_event_type || ""} onChange={(e) => setEditData((d) => ({ ...d, classificationProfile: { ...(d.classificationProfile || {}), primary_event_type: e.target.value } }))} className="mt-1 w-full h-10 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring">
                        {CLASSIFICATION_OPTIONS.primary_event_type.map((option) => (
                          <option key={option} value={option}>{capitalize(option)}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-sm font-medium">Kids policy</label>
                      <select value={editData.classificationProfile?.kids_policy || ""} onChange={(e) => setEditData((d) => ({ ...d, classificationProfile: { ...(d.classificationProfile || {}), kids_policy: e.target.value } }))} className="mt-1 w-full h-10 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring">
                        {CLASSIFICATION_OPTIONS.kids_policy.map((option) => (
                          <option key={option} value={option}>{capitalize(option)}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium">Judgments</label>
                    {editData.classificationProfile?.judgments ? (
                      <div className="mt-1">
                        <JudgmentPicker
                          judgments={editData.classificationProfile.judgments}
                          onChange={(judgments) => setEditData((d) => ({ ...d, classificationProfile: { ...(d.classificationProfile || {}), judgments } }))}
                        />
                        <p className="text-xs text-muted-foreground mt-1">Saving propagates them to the events that inherit this artist's classification.</p>
                      </div>
                    ) : (
                      <p className="mt-1 text-xs text-muted-foreground">Not yet classified with judgments — Classify the artist to get them.</p>
                    )}
                  </div>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <label className="text-sm font-medium">Themes / subjects</label>
                      <input type="text" value={editData.classificationProfile?.themes_subjects || ""} disabled className="mt-1 w-full h-10 rounded-md border border-input bg-muted px-3 text-sm text-muted-foreground" />
                      <p className="text-xs text-muted-foreground mt-1">Coming soon</p>
                    </div>
                    {[
                      ["experience_modalities", "Modalities"],
                      ["cultural_identity", "Cultural identity"],
                      ["target_audience", "Target audience"],
                    ].map(([field, label]) => (
                      <ClassificationMultiSelect
                        key={field}
                        label={label}
                        options={CLASSIFICATION_OPTIONS[field]}
                        value={editData.classificationProfile?.[field] || ""}
                        onChange={(value) => setEditData((d) => ({ ...d, classificationProfile: { ...(d.classificationProfile || {}), [field]: value } }))}
                      />
                    ))}
                    <div>
                      <label className="text-sm font-medium">Spoken language</label>
                      <input type="text" value={editData.classificationProfile?.spoken_language || ""} onChange={(e) => setEditData((d) => ({ ...d, classificationProfile: { ...(d.classificationProfile || {}), spoken_language: e.target.value } }))} placeholder="Comma separated" className="mt-1 w-full h-10 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium">Context</label>
                    <textarea value={editData.classificationProfile?.inferred_context || ""} onChange={(e) => setEditData((d) => ({ ...d, classificationProfile: { ...(d.classificationProfile || {}), inferred_context: e.target.value } }))} rows={3} className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
                  </div>
                </div>
              </div>

              <ModalFooter>
                <Button variant="outline" onClick={() => { setIsEditModalOpen(false); setSelectedArtist(null); }} disabled={isSaving}>
                  Cancel
                </Button>
                <Button onClick={handleSaveEdit} disabled={isSaving}>
                  {isSaving ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving...</>
                  ) : (
                    <><Save className="h-4 w-4 mr-2" />Save</>
                  )}
                </Button>
              </ModalFooter>
            </div>
          )}
        </ModalContent>
      </Modal>

      {/* Delete Modal */}
      <Modal open={isDeleteModalOpen} onOpenChange={setIsDeleteModalOpen}>
        <ModalContent title="Delete Artist">
          <p className="text-muted-foreground">
            Are you sure you want to delete the artist "{displayLabel(selectedArtist?.preferred_name, selectedArtist?.name)}"?
          </p>
          <p className="text-xs text-muted-foreground mt-2">
            The artist is removed from every event, user favorites list and open report. Its record is kept under the
            Deleted tab so the next fetch cannot re-create it.
          </p>

          {deleteImpact && (
            <div className="mt-4 rounded-lg border border-destructive/40 bg-destructive/5 p-3">
              <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-destructive">
                Nothing deleted yet — this would affect
              </p>
              <ul className="mb-3 space-y-0.5 text-xs text-muted-foreground">
                <li>{deleteImpact.events_total} event{deleteImpact.events_total !== 1 ? "s" : ""}</li>
                <li>
                  {deleteImpact.users_affected + deleteImpact.anonymous_users_affected} favorites list
                  {deleteImpact.users_affected + deleteImpact.anonymous_users_affected !== 1 ? "s" : ""}
                </li>
                <li>{deleteImpact.open_reports} open report{deleteImpact.open_reports !== 1 ? "s" : ""}</li>
              </ul>
              {deleteImpact.events.length > 0 && (
                <ul className="max-h-48 space-y-1.5 overflow-y-auto text-xs">
                  {deleteImpact.events.map((ev) => (
                    <ReviewEventLink key={ev.id || ev.title} event={ev} />
                  ))}
                  {deleteImpact.events_total > deleteImpact.events.length && (
                    <li className="text-[10px] italic text-muted-foreground">
                      +{deleteImpact.events_total - deleteImpact.events.length} more not shown
                    </li>
                  )}
                </ul>
              )}
            </div>
          )}

          <ModalFooter className="mt-4">
            <Button variant="outline" onClick={() => { setIsDeleteModalOpen(false); setSelectedArtist(null); setDeleteImpact(null); }} disabled={isDeleting}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={() => handleDelete()} disabled={isDeleting}>
              {isDeleting ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Deleting...</>
              ) : deleteImpact ? (
                "Delete anyway"
              ) : (
                "Delete"
              )}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Change MusicBrainz ID Modal */}
      <Modal open={isMbidModalOpen} onOpenChange={setIsMbidModalOpen}>
        <ModalContent
          title={selectedArtist?.source_ids?.musicbrainz ? "Change MusicBrainz ID" : "Set MusicBrainz ID"}
          className="max-w-xl max-h-[calc(100dvh-1rem)] overflow-y-auto sm:max-h-[90vh]"
        >
          {selectedArtist && (
            <div className="space-y-4">
              <div className="text-sm text-muted-foreground">
                Searching MusicBrainz for <strong>{selectedArtist.name}</strong>. Selecting a candidate will replace
                this artist's MusicBrainz ID and re-run the enricher (country, genres, links).
              </div>

              {selectedArtist.source_ids?.musicbrainz && (
                <div className="rounded-md border border-input bg-muted/30 p-3 text-sm">
                  <p className="text-xs text-muted-foreground">Current MusicBrainz ID</p>
                  <p className="font-mono text-xs truncate">{selectedArtist.source_ids.musicbrainz}</p>
                </div>
              )}

              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="text"
                  value={mbidSearchQuery}
                  onChange={(e) => {
                    setMbidSearchQuery(e.target.value);
                    setMbidSearchLimit(MBID_SEARCH_PAGE);
                  }}
                  placeholder="Search MusicBrainz by artist name..."
                  className="w-full h-10 rounded-md border border-input bg-background pl-9 pr-9 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  autoFocus
                />
                {mbidSearchLoading && (
                  <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />
                )}
              </div>

              {mbidSearchError && (
                <p className="text-xs text-red-600">{mbidSearchError}</p>
              )}

              {mbidSearchResults.length > 0 && (
                <div>
                {mbidResultsSource && mbidResultsSource !== "name" && (
                  <p className="text-xs text-muted-foreground mb-1">
                    Matched via {mbidResultsSource === "spotify+youtube" ? "Spotify & YouTube" : mbidResultsSource === "spotify" ? "Spotify ID" : "YouTube"} — search by name to find others
                  </p>
                )}
                <div className="rounded-md border border-input bg-background max-h-72 overflow-y-auto">
                  {mbidSearchResults.map((artist) => {
                    const parts = [];
                    if (artist.type) parts.push(artist.type);
                    if (artist.country) parts.push(artist.country);
                    if (artist.disambiguation) parts.push(`(${artist.disambiguation})`);
                    return (
                      <button
                        key={artist.id}
                        type="button"
                        onClick={() => handleSelectMbCandidate(artist)}
                        className="w-full text-left px-3 py-2 hover:bg-muted/50 border-b border-border last:border-b-0"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium truncate">{artist.name}</p>
                            {parts.length > 0 && (
                              <p className="text-xs text-muted-foreground truncate">{parts.join(" • ")}</p>
                            )}
                            <p className="font-mono text-xs text-muted-foreground break-all">{artist.id}</p>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            {artist.score != null && (
                              <span className="text-xs text-muted-foreground">{artist.score}%</span>
                            )}
                            <a
                              href={`https://musicbrainz.org/artist/${artist.id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="text-muted-foreground hover:text-foreground"
                              title="Open on MusicBrainz"
                            >
                              <ExternalLink className="h-3.5 w-3.5" />
                            </a>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
                {mbidResultsSource === "name" &&
                  mbidSearchResults.length >= mbidSearchLimit &&
                  mbidSearchLimit < MBID_SEARCH_MAX && (
                    <button
                      type="button"
                      onClick={() => setMbidSearchLimit((l) => Math.min(l + MBID_SEARCH_PAGE, MBID_SEARCH_MAX))}
                      disabled={mbidSearchLoading}
                      className="mt-2 text-xs text-primary hover:underline disabled:opacity-50"
                    >
                      {mbidSearchLoading ? "Loading…" : `Show more (${mbidSearchResults.length} shown)`}
                    </button>
                  )}
                </div>
              )}

              {mbidSelected && (
                <div className="rounded-md border border-input bg-muted/30 p-3 space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-xs text-muted-foreground">Selected candidate</p>
                      <p className="text-sm font-medium truncate">{mbidSelected.name}</p>
                      <p className="font-mono text-xs truncate">{mbidSelected.mbid}</p>
                      {(mbidSelected.type || mbidSelected.country || mbidSelected.disambiguation) && (
                        <p className="text-xs text-muted-foreground mt-1">
                          {[mbidSelected.type, mbidSelected.country, mbidSelected.disambiguation]
                            .filter(Boolean)
                            .join(" • ")}
                        </p>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={resetMbidSelection}
                      className="text-xs text-muted-foreground hover:text-foreground"
                    >
                      Clear
                    </button>
                  </div>

                  {mbidChecking && (
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Loader2 className="h-3 w-3 animate-spin" />
                      Checking availability...
                    </div>
                  )}
                  {!mbidChecking && mbidCheck?.available && (
                    <div className="flex items-center gap-2 text-xs text-green-700">
                      <Check className="h-3 w-3" />
                      Available — no other artist uses this MusicBrainz ID
                    </div>
                  )}
                  {!mbidChecking && mbidCheck && !mbidCheck.available && (
                    <div className="space-y-2">
                      <p className="text-xs font-medium text-red-700">
                        {mbidCheck._error ||
                          "This MusicBrainz ID is already used by another artist, so it can't be assigned here."}
                      </p>
                      {/* Two artists holding the same MBID are the same act — the merge is the fix,
                          not a dead end. The MBID holder is the canonical; the open artist folds in. */}
                      {(mbidCheck.conflicting_artists || []).length > 0 && (
                        <p className="text-xs text-muted-foreground">
                          If it&apos;s the same artist, merge <span className="font-medium text-foreground">{displayLabel(selectedArtist?.preferred_name, selectedArtist?.name)}</span>{" "}
                          into the artist that already holds this ID — {MERGE_EFFECT_COPY}
                        </p>
                      )}
                      {(mbidCheck.conflicting_artists || []).map((a) => (
                        <div
                          key={a.id}
                          className="flex items-center justify-between gap-2 rounded-md border border-input bg-background p-2"
                        >
                          <div className="min-w-0">
                            <p className="truncate text-sm font-medium">{a.name || "(no name)"}</p>
                            <p className="truncate font-mono text-xs text-muted-foreground">{a.id}</p>
                          </div>
                          <MergeIntoButton
                            onClick={() => handleMergeIntoConflict(a)}
                            disabled={dupBusy || mbidSaving}
                            busy={dupBusy}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {!mbidChecking && mbidCheck?.available && (
                <div className="rounded-md border border-amber-200 bg-amber-50 p-3 space-y-2">
                  <p className="text-sm font-medium text-amber-900">
                    Do you want to keep the existing artist data?
                  </p>
                  <p className="text-xs text-amber-700">
                    This affects genres, classification, country, origin country, links, the Spotify/Deezer provider ids, and any photo found from Wikidata or Deezer. If the new MBID points to a different artist, choose <span className="font-semibold">Replace</span> — that data belongs to the old artist, so it is discarded and refetched from MusicBrainz. If it&apos;s the same artist with a corrected ID, choose <span className="font-semibold">Keep</span> — existing data is preserved and new MusicBrainz data is merged on top.
                  </p>
                  <div className="flex gap-2 pt-1">
                    <Button
                      type="button"
                      size="sm"
                      variant={mbidKeepClassification === true ? "default" : "outline"}
                      onClick={() => setMbidKeepClassification(true)}
                    >
                      Keep
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant={mbidKeepClassification === false ? "default" : "outline"}
                      onClick={() => setMbidKeepClassification(false)}
                    >
                      Replace
                    </Button>
                  </div>
                </div>
              )}

              <ModalFooter>
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsMbidModalOpen(false);
                    resetMbidSelection();
                  }}
                  disabled={mbidSaving}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveMbid}
                  disabled={
                    mbidSaving ||
                    mbidChecking ||
                    !mbidSelected ||
                    !mbidCheck?.available ||
                    mbidKeepClassification === null
                  }
                >
                  {mbidSaving ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving & re-enriching...</>
                  ) : (
                    <><Save className="h-4 w-4 mr-2" />Save</>
                  )}
                </Button>
              </ModalFooter>
            </div>
          )}
        </ModalContent>
      </Modal>
    </div>
  );
}
